/**
 * ErrorBoundary Component for Inspector v2 Services
 *
 * Catches JavaScript errors in child components and displays a fallback UI
 * instead of crashing the entire inspector. Follows the pattern from
 * @babylonjs/inspector-v2's errorBoundary.tsx.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary name="StageControls">
 *   <StageControlsContent />
 * </ErrorBoundary>
 * ```
 */

import { Button, makeStyles, Text, tokens } from "@fluentui/react-components";
import { ErrorCircle20Regular } from "@fluentui/react-icons";
import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";

const useStyles = makeStyles({
  details: {
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    color: tokens.colorNeutralForeground3,
    fontFamily: "monospace",
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalS,
    maxHeight: "100px",
    maxWidth: "100%",
    overflow: "auto",
    padding: tokens.spacingHorizontalS,
    whiteSpace: "pre-wrap",
  },
  icon: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: "32px",
    marginBottom: tokens.spacingVerticalS,
  },
  message: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    marginBottom: tokens.spacingVerticalM,
    maxWidth: "280px",
    textAlign: "center",
  },
  root: {
    alignItems: "center",
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    justifyContent: "center",
    minHeight: "100px",
    padding: tokens.spacingHorizontalM,
  },
  title: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalXS,
  },
});

export interface ErrorBoundaryProps {
  /** Child components to render */
  children: ReactNode;
  /** Optional fallback UI to show on error */
  fallback?: ReactNode;
  /** Optional name for identifying this boundary in logs */
  name?: string;
  /** Optional callback when an error occurs */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  hasError: boolean;
}

/**
 * Error boundary component that catches JavaScript errors in child components
 * and displays a fallback UI instead of crashing the entire application.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, errorInfo: null, hasError: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error, hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Log the error with boundary name for easier debugging
    const boundaryName = this.props.name ?? "ErrorBoundary";
    console.error(`[${boundaryName}] Error caught:`, error, errorInfo);

    // Call optional error callback
    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = (): void => {
    this.setState({ error: null, errorInfo: null, hasError: false });
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      // If a custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <ErrorFallback error={this.state.error} onRetry={this.handleRetry} />
      );
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error: Error | null;
  onRetry: () => void;
}

function ErrorFallback({ error, onRetry }: ErrorFallbackProps): ReactNode {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      <ErrorCircle20Regular className={styles.icon} />
      <Text className={styles.title}>Something went wrong</Text>
      <Text className={styles.message}>
        An error occurred in this panel. You can try again or continue using
        other parts of the inspector.
      </Text>
      <Button appearance="primary" onClick={onRetry}>
        Try Again
      </Button>
      {error && <div className={styles.details}>{error.message}</div>}
    </div>
  );
}
